import React, { useState, useEffect, useMemo } from 'react';
import { 
    Calendar, 
    User, 
    Download, 
    Table, 
    BarChart2,
    ChevronUp,
    ChevronDown,
    Filter,
    X
} from 'react-feather';
import { projectApi } from '../../services/api';
import { exportTimesheetToCSV, exportGroupedTimesheetToCSV } from '../../utils/csvExport';
import { Button } from '../ui/button';
import './BillingTab.css';

const BillingTab = ({ projectId, projectName, users = [] }) => {
    const [timesheetData, setTimesheetData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Initialize "This Month" dates by default
    const getThisMonthDates = () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const end = now.toISOString().split('T')[0];
        return { start, end };
    };
    
    const initialDates = getThisMonthDates();
    
    // Filter states
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [activeQuickFilter, setActiveQuickFilter] = useState('thisMonth'); // Track active quick filter
    
    // View states
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'summary'
    const [summaryGroupBy, setSummaryGroupBy] = useState('user'); // 'user', 'month', 'task'
    const [groupByTask, setGroupByTask] = useState(false); // Group same tasks together
    
    // Table sorting
    const [sortColumn, setSortColumn] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // Fetch timesheet data
    useEffect(() => {
        fetchTimesheetData();
    }, [projectId, startDate, endDate, selectedUserId]);

    const fetchTimesheetData = async () => {
        try {
            setLoading(true);
            setError('');
            
            const filters = {};
            if (startDate) filters.startDate = startDate;
            if (endDate) filters.endDate = endDate;
            if (selectedUserId) filters.userId = selectedUserId;
            
            const data = await projectApi.getTimesheet(projectId, filters);
            setTimesheetData(data || []);
        } catch (err) {
            console.error('Error fetching timesheet data:', err);
            setError('Failed to load timesheet data');
        } finally {
            setLoading(false);
        }
    };

    // Quick filter handlers
    const applyQuickFilter = (filterType, e) => {
        // Prevent any default form submission behavior
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        const now = new Date();
        let start = '';
        let end = '';

        switch (filterType) {
            case 'thisMonth':
                start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                end = now.toISOString().split('T')[0];
                break;
            case 'lastMonth':
                const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                start = lastMonth.toISOString().split('T')[0];
                end = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
                break;
            case 'thisYear':
                start = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
                end = now.toISOString().split('T')[0];
                break;
            case 'allTime':
                start = '';
                end = '';
                break;
            default:
                return;
        }

        setStartDate(start);
        setEndDate(end);
        setActiveQuickFilter(filterType); // Set active filter
        setCurrentPage(1);
    };

    // Clear all filters
    const clearFilters = () => {
        setStartDate('');
        setEndDate('');
        setSelectedUserId('');
        setActiveQuickFilter(null); // Clear active filter
        setCurrentPage(1);
    };

    // Table sorting
    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    // Format date for display (moved before useMemo)
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Group data by task if enabled
    const groupedData = useMemo(() => {
        if (!groupByTask) {
            return null;
        }

        const grouped = {};
        timesheetData.forEach(entry => {
            const taskId = entry.task_id || 'unknown';
            if (!grouped[taskId]) {
                grouped[taskId] = {
                    task_id: taskId,
                    task_title: entry.task_title || 'Unknown Task',
                    task_display_id: entry.task_display_id || null,
                    feature_tag_name: entry.feature_tag_name || null,
                    feature_tag_color: entry.feature_tag_color || null,
                    totalHours: 0,
                    entries: [],
                    users: new Set(),
                    dates: []
                };
            }
            grouped[taskId].totalHours += parseFloat(entry.hours) || 0;
            grouped[taskId].entries.push(entry);
            if (entry.user_name) {
                grouped[taskId].users.add(entry.user_name);
            }
            if (entry.date) {
                grouped[taskId].dates.push(entry.date);
            }
        });

        // Convert to array and format
        return Object.values(grouped).map(group => {
            const sortedDates = group.dates.sort((a, b) => new Date(a) - new Date(b));
            const dateRange = sortedDates.length > 0
                ? sortedDates.length === 1
                    ? formatDate(sortedDates[0])
                    : `${formatDate(sortedDates[0])} - ${formatDate(sortedDates[sortedDates.length - 1])}`
                : '-';

            return {
                id: `grouped-${group.task_id}`,
                task_id: group.task_id,
                task_title: group.task_title,
                task_display_id: group.task_display_id,
                feature_tag_name: group.feature_tag_name,
                feature_tag_color: group.feature_tag_color,
                hours: group.totalHours,
                entry_count: group.entries.length,
                users: Array.from(group.users).join(', '),
                date_range: dateRange,
                earliest_date: sortedDates[0] || null,
                latest_date: sortedDates[sortedDates.length - 1] || null,
                _isGrouped: true
            };
        });
    }, [timesheetData, groupByTask]);

    // Sorted and filtered data
    const sortedData = useMemo(() => {
        const data = groupByTask && groupedData ? [...groupedData] : [...timesheetData];
        
        data.sort((a, b) => {
            let aVal = a[sortColumn];
            let bVal = b[sortColumn];
            
            // Handle date sorting - use earliest_date for grouped data
            if (sortColumn === 'date' || sortColumn === 'earliest_date') {
                const dateCol = a._isGrouped ? 'earliest_date' : 'date';
                aVal = a[dateCol] ? new Date(a[dateCol]).getTime() : 0;
                bVal = b[dateCol] ? new Date(b[dateCol]).getTime() : 0;
            }
            
            // Handle number sorting
            if (sortColumn === 'hours') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }
            
            // Handle string sorting
            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }
            
            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
            } else {
                return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
            }
        });
        
        return data;
    }, [timesheetData, groupByTask, groupedData, sortColumn, sortDirection]);

    // Paginated data
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedData, currentPage]);

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    // Summary calculations
    const summaryData = useMemo(() => {
        if (summaryGroupBy === 'user') {
            const grouped = {};
            timesheetData.forEach(entry => {
                const key = entry.user_id || 'unknown';
                if (!grouped[key]) {
                    grouped[key] = {
                        id: key,
                        name: entry.user_name || 'Unknown User',
                        email: entry.user_email || '',
                        totalHours: 0,
                        entries: 0
                    };
                }
                grouped[key].totalHours += parseFloat(entry.hours) || 0;
                grouped[key].entries += 1;
            });
            return Object.values(grouped).sort((a, b) => b.totalHours - a.totalHours);
        } else if (summaryGroupBy === 'month') {
            const grouped = {};
            timesheetData.forEach(entry => {
                if (!entry.date) return;
                const date = new Date(entry.date);
                const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                
                if (!grouped[key]) {
                    grouped[key] = {
                        id: key,
                        month: monthName,
                        totalHours: 0,
                        entries: 0
                    };
                }
                grouped[key].totalHours += parseFloat(entry.hours) || 0;
                grouped[key].entries += 1;
            });
            return Object.values(grouped).sort((a, b) => b.id.localeCompare(a.id));
        } else if (summaryGroupBy === 'task') {
            const grouped = {};
            timesheetData.forEach(entry => {
                const key = entry.task_id || 'unknown';
                if (!grouped[key]) {
                    grouped[key] = {
                        id: key,
                        taskTitle: entry.task_title || 'Unknown Task',
                        taskDisplayId: entry.task_display_id || '',
                        totalHours: 0,
                        entries: 0
                    };
                }
                grouped[key].totalHours += parseFloat(entry.hours) || 0;
                grouped[key].entries += 1;
            });
            return Object.values(grouped).sort((a, b) => b.totalHours - a.totalHours);
        } else if (summaryGroupBy === 'feature') {
            const grouped = {};
            timesheetData.forEach(entry => {
                const key = entry.feature_tag_id || entry.feature_tag_name || 'untagged';
                const tagName = entry.feature_tag_name || 'Untagged';
                const tagColor = entry.feature_tag_color || null;
                
                if (!grouped[key]) {
                    grouped[key] = {
                        id: key,
                        featureName: tagName,
                        featureColor: tagColor,
                        totalHours: 0,
                        entries: 0
                    };
                }
                grouped[key].totalHours += parseFloat(entry.hours) || 0;
                grouped[key].entries += 1;
            });
            return Object.values(grouped).sort((a, b) => {
                // Put "Untagged" at the end
                if (a.featureName === 'Untagged' && b.featureName !== 'Untagged') return 1;
                if (a.featureName !== 'Untagged' && b.featureName === 'Untagged') return -1;
                return b.totalHours - a.totalHours;
            });
        }
        return [];
    }, [timesheetData, summaryGroupBy]);

    // Statistics
    const stats = useMemo(() => {
        const totalHours = timesheetData.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0);
        const uniqueDates = new Set(timesheetData.map(e => e.date).filter(Boolean)).size;
        const avgHoursPerDay = uniqueDates > 0 ? totalHours / uniqueDates : 0;
        
        return {
            totalHours: totalHours.toFixed(2),
            totalEntries: timesheetData.length,
            avgHoursPerDay: avgHoursPerDay.toFixed(2),
            uniqueUsers: new Set(timesheetData.map(e => e.user_id).filter(Boolean)).size
        };
    }, [timesheetData]);

    // Export handler
    const handleExport = () => {
        if (groupByTask && groupedData) {
            // Export grouped data
            exportGroupedTimesheetToCSV(groupedData, projectName || 'project');
        } else {
            // Export individual entries
            exportTimesheetToCSV(timesheetData, projectName || 'project');
        }
    };


    // Get sort icon
    const getSortIcon = (column) => {
        if (sortColumn !== column) return null;
        return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    if (loading) {
        return (
            <div className="billing-tab-loading">
                <div className="loading-spinner"></div>
                <p>Loading timesheet data...</p>
            </div>
        );
    }

    return (
        <div className="billing-tab">
            {/* Statistics Cards */}
            <div className="billing-stats">
                <div className="stat-card">
                    <div className="stat-value">{stats.totalHours}h</div>
                    <div className="stat-label">Total Hours</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{stats.totalEntries}</div>
                    <div className="stat-label">Total Entries</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{stats.avgHoursPerDay}h</div>
                    <div className="stat-label">Avg Hours/Day</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{stats.uniqueUsers}</div>
                    <div className="stat-label">Team Members</div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="billing-filters">
                <div className="filters-header">
                    <div className="filters-title">
                        <Filter size={16} />
                        <span>Filters</span>
                    </div>
                    {(startDate || endDate || selectedUserId) && (
                        <Button
                            type="button"
                            variant="neutral-tertiary"
                            size="sm"
                            onClick={clearFilters}
                            icon={<X size={14} />}
                        >
                            Clear Filters
                        </Button>
                    )}
                </div>
                
                <div className="filters-content">
                    <div className="filter-group date-range-group">
                        <label>Date Range</label>
                        <div className="date-inputs">
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                    setActiveQuickFilter(null); // Clear active filter when manually changed
                                    setCurrentPage(1);
                                }}
                                placeholder="Start Date"
                            />
                            <span>to</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                    setActiveQuickFilter(null); // Clear active filter when manually changed
                                    setCurrentPage(1);
                                }}
                                placeholder="End Date"
                            />
                        </div>
                    </div>

                    <div className="filter-group">
                        <label>User</label>
                        <select
                            value={selectedUserId}
                            onChange={(e) => {
                                setSelectedUserId(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">All Users</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.display_name || user.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group quick-filters">
                        <label>Quick Filters</label>
                        <div className="quick-filter-buttons">
                            <Button
                                type="button"
                                variant={activeQuickFilter === 'thisMonth' ? 'primary' : 'neutral-tertiary'}
                                size="sm"
                                onClick={(e) => applyQuickFilter('thisMonth', e)}
                                className={activeQuickFilter === 'thisMonth' ? 'quick-filter-active' : ''}
                            >
                                This Month
                            </Button>
                            <Button
                                type="button"
                                variant={activeQuickFilter === 'lastMonth' ? 'primary' : 'neutral-tertiary'}
                                size="sm"
                                onClick={(e) => applyQuickFilter('lastMonth', e)}
                                className={activeQuickFilter === 'lastMonth' ? 'quick-filter-active' : ''}
                            >
                                Last Month
                            </Button>
                            <Button
                                type="button"
                                variant={activeQuickFilter === 'thisYear' ? 'primary' : 'neutral-tertiary'}
                                size="sm"
                                onClick={(e) => applyQuickFilter('thisYear', e)}
                                className={activeQuickFilter === 'thisYear' ? 'quick-filter-active' : ''}
                            >
                                This Year
                            </Button>
                            <Button
                                type="button"
                                variant={activeQuickFilter === 'allTime' ? 'primary' : 'neutral-tertiary'}
                                size="sm"
                                onClick={(e) => applyQuickFilter('allTime', e)}
                                className={activeQuickFilter === 'allTime' ? 'quick-filter-active' : ''}
                            >
                                All Time
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* View Toggle and Export */}
            <div className="billing-actions">
                <div className="view-toggle-group">
                    <Button
                        type="button"
                        variant={viewMode === 'table' ? 'brand-primary' : 'neutral-tertiary'}
                        onClick={() => setViewMode('table')}
                        icon={<Table size={16} />}
                    >
                        Table View
                    </Button>
                    <Button
                        type="button"
                        variant={viewMode === 'summary' ? 'brand-primary' : 'neutral-tertiary'}
                        onClick={() => setViewMode('summary')}
                        icon={<BarChart2 size={16} />}
                    >
                        Summary View
                    </Button>
                </div>

                {viewMode === 'table' && (
                    <div className="table-options">
                        <label className="toggle-label">
                            <input
                                type="checkbox"
                                checked={groupByTask}
                                onChange={(e) => {
                                    setGroupByTask(e.target.checked);
                                    setCurrentPage(1);
                                }}
                            />
                            <span>Group by Task</span>
                        </label>
                    </div>
                )}

                {viewMode === 'summary' && (
                    <div className="summary-group-toggle">
                        <label>Group By:</label>
                        <select
                            value={summaryGroupBy}
                            onChange={(e) => setSummaryGroupBy(e.target.value)}
                        >
                            <option value="user">User</option>
                            <option value="month">Month</option>
                            <option value="task">Task</option>
                            <option value="feature">Feature</option>
                        </select>
                    </div>
                )}

                <Button
                    type="button"
                    variant="brand-primary"
                    onClick={handleExport}
                    icon={<Download size={16} />}
                >
                    Export CSV
                </Button>
            </div>

            {error && (
                <div className="billing-error">
                    {error}
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="billing-table-container">
                    <table className="billing-table">
                        <thead>
                            <tr>
                                {groupByTask ? (
                                    <>
                                        <th onClick={() => handleSort('task_title')} className="sortable">
                                            <span className="th-content">Task {getSortIcon('task_title')}</span>
                                        </th>
                                        <th>Feature Tag</th>
                                        <th onClick={() => handleSort('hours')} className="sortable">
                                            <span className="th-content">Total Hours {getSortIcon('hours')}</span>
                                        </th>
                                        <th>Users</th>
                                        <th onClick={() => handleSort('earliest_date')} className="sortable">
                                            <span className="th-content">Date Range {getSortIcon('earliest_date')}</span>
                                        </th>
                                        <th>Entries</th>
                                    </>
                                ) : (
                                    <>
                                        <th onClick={() => handleSort('date')} className="sortable">
                                            <span className="th-content">Date {getSortIcon('date')}</span>
                                        </th>
                                        <th onClick={() => handleSort('user_name')} className="sortable">
                                            <span className="th-content">User {getSortIcon('user_name')}</span>
                                        </th>
                                        <th onClick={() => handleSort('task_title')} className="sortable">
                                            <span className="th-content">Task {getSortIcon('task_title')}</span>
                                        </th>
                                        <th>Feature Tag</th>
                                        <th onClick={() => handleSort('hours')} className="sortable">
                                            <span className="th-content">Hours {getSortIcon('hours')}</span>
                                        </th>
                                        <th>Notes</th>
                                        <th>Status</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length === 0 ? (
                                <tr>
                                    <td colSpan={groupByTask ? 6 : 7} className="no-data">
                                        No timesheet data found
                                    </td>
                                </tr>
                            ) : (
                                paginatedData.map(entry => {
                                    if (entry._isGrouped) {
                                        // Grouped row
                                        return (
                                            <tr key={entry.id} className="grouped-row">
                                                <td>
                                                    {entry.task_display_id && (
                                                        <span className="task-id">{entry.task_display_id}</span>
                                                    )}
                                                    {entry.task_title}
                                                </td>
                                                <td>
                                                    {entry.feature_tag_name ? (
                                                        <span 
                                                            className="feature-tag-badge"
                                                            style={{ 
                                                                backgroundColor: entry.feature_tag_color || '#6366f1',
                                                                color: 'white'
                                                            }}
                                                        >
                                                            {entry.feature_tag_name}
                                                        </span>
                                                    ) : (
                                                        <span className="no-feature-tag">-</span>
                                                    )}
                                                </td>
                                                <td className="hours-cell">{parseFloat(entry.hours || 0).toFixed(2)}h</td>
                                                <td className="users-cell" title={entry.users}>
                                                    {entry.users || '-'}
                                                </td>
                                                <td className="date-range-cell">{entry.date_range}</td>
                                                <td>{entry.entry_count}</td>
                                            </tr>
                                        );
                                    } else {
                                        // Regular row
                                        return (
                                            <tr key={entry.id}>
                                                <td>{formatDate(entry.date)}</td>
                                                <td>{entry.user_name}</td>
                                                <td>
                                                    {entry.task_display_id && (
                                                        <span className="task-id">{entry.task_display_id}</span>
                                                    )}
                                                    {entry.task_title}
                                                </td>
                                                <td>
                                                    {entry.feature_tag_name ? (
                                                        <span 
                                                            className="feature-tag-badge"
                                                            style={{ 
                                                                backgroundColor: entry.feature_tag_color || '#6366f1',
                                                                color: 'white'
                                                            }}
                                                        >
                                                            {entry.feature_tag_name}
                                                        </span>
                                                    ) : (
                                                        <span className="no-feature-tag">-</span>
                                                    )}
                                                </td>
                                                <td className="hours-cell">{parseFloat(entry.hours || 0).toFixed(2)}h</td>
                                                <td className="notes-cell" title={entry.notes}>
                                                    {entry.notes || '-'}
                                                </td>
                                                <td>
                                                    <span className={`status-badge status-${entry.status}`}>
                                                        {entry.status || 'pending'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    }
                                })
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="billing-pagination">
                            <Button
                                type="button"
                                variant="neutral-tertiary"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </Button>
                            <span className="pagination-info">
                                Page {currentPage} of {totalPages} ({sortedData.length} entries)
                            </span>
                            <Button
                                type="button"
                                variant="neutral-tertiary"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Summary View */}
            {viewMode === 'summary' && (
                <div className="billing-summary">
                    {summaryData.length === 0 ? (
                        <div className="no-data">No data available for summary</div>
                    ) : (
                        <div className="summary-list">
                            {summaryData.map(item => (
                                <div key={item.id} className="summary-item">
                                    <div className="summary-header">
                                        {summaryGroupBy === 'user' && (
                                            <>
                                                <div className="summary-title">{item.name}</div>
                                                {item.email && (
                                                    <div className="summary-subtitle">{item.email}</div>
                                                )}
                                            </>
                                        )}
                                        {summaryGroupBy === 'month' && (
                                            <div className="summary-title">{item.month}</div>
                                        )}
                                        {summaryGroupBy === 'task' && (
                                            <>
                                                <div className="summary-title">
                                                    {item.taskDisplayId && (
                                                        <span className="task-id">{item.taskDisplayId}</span>
                                                    )}
                                                    {item.taskTitle}
                                                </div>
                                            </>
                                        )}
                                        {summaryGroupBy === 'feature' && (
                                            <div className="summary-title">
                                                {item.featureName === 'Untagged' ? (
                                                    <span className="no-feature-tag">{item.featureName}</span>
                                                ) : (
                                                    <span 
                                                        className="feature-tag-badge"
                                                        style={{ 
                                                            backgroundColor: item.featureColor || '#6366f1',
                                                            color: 'white'
                                                        }}
                                                    >
                                                        {item.featureName}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="summary-stats">
                                        <div className="summary-stat">
                                            <span className="stat-label">Total Hours:</span>
                                            <span className="stat-value">{item.totalHours.toFixed(2)}h</span>
                                        </div>
                                        <div className="summary-stat">
                                            <span className="stat-label">Entries:</span>
                                            <span className="stat-value">{item.entries}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BillingTab;

